import { Checkbox, FormControlLabel, Link, TextField } from '@material-ui/core';
import DialogContentText from '@material-ui/core/DialogContentText';
import * as React from 'react';
import type { KitType } from '../actions/getKitTypes';
import updateKit from '../actions/updateKit';
import { sendRequest } from '../api/websocketApi';
import { handleDownload } from '../helpers/handleDownload';
import type { Kit } from '../hooks/useKits';
import { ReactComponent as TestTubeIcon } from '../icons/testTube.svg';
import useStore from '../store/store';
import Button from './Button';
import Dialog from './Dialog';
import TermsAndConditions from './TermsAndConditions';
import UploadTRFDialog from './UploadTRFDialog';

interface RegisterKitProps {
    open: boolean;
    onClose: () => void;
    kit: Kit;
    onKitRegistered: () => void;
    isAdmin?: boolean;
}

const RegisterKitForm = ({
    open,
    onClose,
    kit,
    onKitRegistered,
    isAdmin = false,
}: RegisterKitProps) => {
    const kitTypes = useStore((state) => state.kitTypes);
    const [isUploadTRFDialogOpen, setIsUploadTRFDialogOpen] =
        React.useState(false);
    const [TRFFile, setTRFFile] = React.useState<File | null>(null);
    const [isTermsAndConditionsChecked, setIsTermsAndConditionsChecked] =
        React.useState(false);
    const [isTermsAndConditionsOpen, setIsTermsAndConditionsOpen] =
        React.useState(false);
    const [termsAndConditionsContent, setTermsAndConditionsContent] =
        React.useState('');
    const [termsAndConditionsExtension, setTermsAndConditionsExtension] =
        React.useState('');
    const [termsAndConditionsVersion, setTermsAndConditionsVersion] =
        React.useState(0);
    const setFormError = useStore.getState().setFormError;

    React.useEffect(() => {
        interface APIResponse {
            success: boolean;
            errCode?: 'invalid_token' | 'auth_failed';
            errDetails?: string;
            content?: string;
            extension?: string;
            version?: number;
        }

        sendRequest(
            {
                endpoint: '/terms/get',
            },
            (response: APIResponse) => {
                if (
                    response.success &&
                    response.content &&
                    response.extension &&
                    response.version
                ) {
                    setTermsAndConditionsContent(response.content);
                    setTermsAndConditionsExtension(response.extension);
                    setTermsAndConditionsVersion(response.version);
                } else {
                    setFormError(response.errCode || 'Unknown error');
                }
            }
        );
    }, [setFormError]);

    const handleCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        setIsTermsAndConditionsChecked(event.target.checked);
    };

    const handleSubmit = async (e: React.SyntheticEvent) => {
        e.preventDefault();

        const target = e.target as typeof e.target & {
            sampleId: { value: string };
            nickname?: { value: string };
        };
        const sampleId = target.sampleId.value;
        const nickname = target.nickname?.value?.trim();

        try {
            await updateKit({
                kitId: kit.KitId,
                sampleId,
                nickname,
                termsVersion: termsAndConditionsVersion,
                ...(TRFFile && { trf: TRFFile }),
                isAdmin,
            });
            onKitRegistered();
            onClose();
        } catch (error) {
            console.error('Failed to update kit:', error);
        }
    };

    const handleTRFUploaded = (file: File) => {
        setTRFFile(file);
        setIsUploadTRFDialogOpen(false);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                onSubmit={handleSubmit}
                title="Register Test"
                icon={<TestTubeIcon />}
                submitDisabled={!TRFFile || !isTermsAndConditionsChecked}
            >
                <DialogContentText component="div">
                    {(() => {
                        const kitType = kitTypes.find(
                            (kitType: KitType) =>
                                kitType.KitTypeId === kit.KitTypeId
                        );

                        const trfFilePath = kitType?.TrfFilePath;

                        return (
                            <>
                                <p>
                                    <b>Test Type:</b> {kitType?.Type ?? 'N/A'}
                                </p>
                                <div>
                                    <b>Test ID:</b>{' '}
                                    {kitType?.KitTypeCatalogueId ?? 'N/A'}
                                    <span
                                        style={{
                                            textDecoration: 'none',
                                            float: 'right',
                                        }}
                                    >
                                        <Button
                                            onClick={() =>
                                                handleDownload(
                                                    trfFilePath,
                                                    isAdmin,
                                                    true
                                                )
                                            }
                                            size="small"
                                            disabled={!trfFilePath}
                                        >
                                            Download TRF Form
                                        </Button>
                                    </span>
                                </div>
                            </>
                        );
                    })()}
                </DialogContentText>
                <TextField
                    margin="dense"
                    id="sampleId"
                    name="sampleId"
                    label="Sample ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    required
                    inputProps={{
                        maxLength: 20,
                    }}
                />
                <TextField
                    margin="dense"
                    id="nickname"
                    name="nickname"
                    label="Nickname"
                    type="text"
                    fullWidth
                    variant="outlined"
                    defaultValue={kit.Nickname}
                    inputProps={{
                        maxLength: 100,
                    }}
                />
                <div style={{ marginTop: '1rem' }}>
                    <Button
                        onClick={() => setIsUploadTRFDialogOpen(true)}
                        size="small"
                    >
                        Upload TRF
                    </Button>
                    {TRFFile && <p>TRF submitted.</p>}
                </div>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isTermsAndConditionsChecked}
                            onChange={handleCheckboxChange}
                            color="primary"
                            required
                        />
                    }
                    style={{ marginTop: '20px' }}
                    label={
                        <span>
                            Please tick this box to indicate that you have read
                            and agree to the{' '}
                            <Link
                                component="button"
                                type="button"
                                style={{ fontSize: 'inherit' }}
                                onClick={() =>
                                    setIsTermsAndConditionsOpen(true)
                                }
                                underline="always"
                            >
                                terms and conditions
                            </Link>
                            .
                        </span>
                    }
                />
            </Dialog>
            <UploadTRFDialog
                open={isUploadTRFDialogOpen}
                onClose={() => setIsUploadTRFDialogOpen(false)}
                onSubmit={handleTRFUploaded}
            />
            <TermsAndConditions
                open={isTermsAndConditionsOpen}
                onClose={() => setIsTermsAndConditionsOpen(false)}
                content={termsAndConditionsContent}
                extension={termsAndConditionsExtension}
                version={termsAndConditionsVersion}
            />
        </>
    );
};

export default RegisterKitForm;
